// <sc-rotator> — the spec's "Karussell-Hero": a fixed frame where one word swaps.
// Server-renders the first word (readable without JS), hydrates, then cycles with
// a calm vertical shared-axis crossfade (WAAPI). Honours prefers-reduced-motion
// (stays on the first word). Validates the reactive core in M0.

import { html } from "../core/html";
import type { TemplateResult } from "../core/html";
import { signal } from "../core/signal";
import type { Signal } from "../core/signal";
import type { ComponentDef } from "../core/ssr";

export interface RotatorProps {
  words: string[];
  intervalMs?: number;
}
interface RotatorState {
  word: Signal<string>;
  words: string[];
  intervalMs: number;
}

const EASE_OUT = "cubic-bezier(0.22, 1, 0.36, 1)";
const EASE_IN = "cubic-bezier(0.64, 0, 0.78, 0)";

export const rotatorDef: ComponentDef<RotatorProps, RotatorState> = {
  tag: "sc-rotator",
  styles: `
    :host { display:inline; font:inherit; color:var(--accent-text,#54B7B1); font-style:normal; }
    .w { display:inline-block; will-change:transform, opacity, filter; }
  `,
  create(props: RotatorProps): RotatorState {
    const words = Array.isArray(props.words) && props.words.length ? props.words : [""];
    return { word: signal(words[0] ?? ""), words, intervalMs: props.intervalMs ?? 2400 };
  },
  view(s: RotatorState): TemplateResult {
    return html`<span class="w">${s.word}</span>`;
  },
  setup(s: RotatorState, host: HTMLElement): void {
    if (s.words.length < 2) return;
    if (typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let i = 0;
    setInterval(() => {
      i = (i + 1) % s.words.length;
      const next = s.words[i] ?? "";
      const node = host.shadowRoot?.querySelector(".w") as HTMLElement | null;
      if (!node || typeof node.animate !== "function") {
        s.word.value = next;
        return;
      }
      node.getAnimations?.().forEach((a) => a.cancel());
      const exit = node.animate(
        [
          { opacity: 1, transform: "translateY(0)", filter: "blur(0px)" },
          { opacity: 0, transform: "translateY(-0.4em)", filter: "blur(3px)" },
        ],
        { duration: 200, easing: EASE_IN, fill: "forwards" },
      );
      exit.finished
        .then(() => {
          s.word.value = next; // the signal swaps the visible text while it is invisible
          node.animate(
            [
              { opacity: 0, transform: "translateY(0.45em)", filter: "blur(3px)" },
              { opacity: 1, transform: "translateY(0)", filter: "blur(0px)" },
            ],
            { duration: 380, easing: EASE_OUT, fill: "forwards" },
          );
        })
        .catch(() => {
          s.word.value = next;
        });
    }, s.intervalMs);
  },
};
